import { useState, useRef, useEffect, useCallback } from 'react';
import { useRegisterOverlay } from '@/stores/overlayStore';
import {
  ChevronLeft,
  Check,
  Trash2,
  User,
  Users,
  CalendarClock,
  MapPin,
  FileText,
  CaseSensitive,
  Sparkles,
  AlertCircle,
  ChevronDown,
} from 'lucide-react';
import {
  Button,
  Input,
  Select,
  Switch,
  Modal,
  DatePicker,
  TaskEventDateIcon,
  TimePicker,
  RadioButton,
} from '@/components/ui';
import { ProfileAvatar } from '@/components/domain/shared/ProfileAvatar';
import { resolveAiChildName, type DisambiguationMap } from '@/lib/familyChild';
import { useUserStore } from '@/stores/userStore';
import { LinksSection } from '@/components/domain/shared/LinksSection';
import { normalizeUrl, linksFromScalar, type LinkEntry } from '@/lib/links';
import type { ReviewItem } from '@/types';
import { RECURRENCE_COPY } from '@/constants/recurrenceCopy';
import { isMemoryFilled, MEMORY_FILL_CAPTION } from '@/lib/memoryFill';
import {
  DATE_ORDER_MESSAGE,
  TIME_ORDER_MESSAGE,
  useDateTimeFields,
} from './useDateTimeFields';

/**
 * The series delete actions for an occurrence of a recurring event
 * (YAR-443). The caller passes them only for series events and only when the
 * user may delete events — the same `canDelete` gate as `onDelete`. With
 * them, the trash can asks "This event / This and following events / All
 * events"; "This event" is the plain `onDelete`.
 */
export interface SeriesDeleteAction {
  /** How many sessions "This and following events" would remove. */
  countFollowing: () => Promise<number>;
  /** Deletes them, then closes the form and refreshes like `onDelete`. */
  deleteFollowing: () => Promise<void>;
  /** How many sessions "All events" would remove, past ones included. */
  countAll: () => Promise<number>;
  /** Deletes every session, then closes and refreshes like `onDelete`. */
  deleteAll: () => Promise<void>;
}

type DeleteScope = 'this' | 'following' | 'all';

// End-before-start messages (YAR-382) live with the shared date slice.

/** Data emitted when the user submits the form. */
export interface ItemFormData {
  type: 'task' | 'event';
  title: string;
  /** Names of assigned children, in insertion order. Empty = none. */
  child_names: string[];
  /**
   * Household member user IDs that should be tagged on this task/event.
   * Empty = none. The PRIMARY uses this dropdown to assign caregivers
   * who then receive a "you were tagged on X" notification.
   */
  member_user_ids: string[];
  description: string | null;
  start_datetime: string | null;
  end_datetime: string | null;
  due_datetime: string | null;
  location: string | null;
  /** Ordered links; the consumer derives the scalar `link` cache. */
  links: { title: string | null; url: string }[];
  related_event_id: string | null;
  is_time_unknown: boolean;
}

export interface AvailableMember {
  user_id: string;
  display_name: string;
  email: string | null;
  avatar_emoji?: string | null;
  avatar_color?: string | null;
  avatar_image_path?: string | null;
}

export interface ItemFormModalProps {
  mode: 'create' | 'edit';
  /**
   * Allow switching the Task/Event type while editing. Only safe for staged
   * `review_items` (a single row with a mutable `type` column). Committed
   * tasks/events live in separate tables and the edit-save path can't convert
   * between them, so those callers leave this `false` and the type stays
   * locked in edit mode (YAR-272). Create mode is always switchable.
   */
  allowTypeChange?: boolean;
  initialType?: 'task' | 'event';
  initialData?: ReviewItem | null;
  presetRelatedEventId?: string | null;
  presetRelatedEventTitle?: string;
  availableEvents: Array<{ id: string; title: string }>;
  availableChildren: Array<{
    name: string;
    emoji_avatar: string | null;
    emoji_color?: string | null;
    avatar_image_path?: string | null;
  }>;
  /**
   * Optional map of `ai_name.toLowerCase()` → canonical family child
   * name, set by the user via the ambiguous-name picker on the parent
   * modal. Honored when normalizing initial child_names so an
   * ambiguous mention pre-selects the child the user already chose.
   */
  disambiguation?: DisambiguationMap;
  /**
   * Household members the form can tag. Includes self. When omitted,
   * the Members section falls back to the read-only "Me" placeholder.
   */
  availableMembers?: AvailableMember[];
  /** User IDs already assigned (edit mode); empty for create. */
  initialMemberIds?: string[];
  /**
   * Called when the user submits the form.
   * The consumer is responsible for calling the appropriate service (emailService, scheduleService, etc.).
   */
  onSubmit: (data: ItemFormData) => Promise<void>;
  onDelete?: () => Promise<void>;
  /** Series events only; see {@link SeriesDeleteAction}. */
  seriesDelete?: SeriesDeleteAction;
  onBack: () => void;
  /** Default date to pre-populate when creating (YYYY-MM-DD). */
  defaultDate?: string;
}

export function ItemFormModal({
  mode,
  allowTypeChange = false,
  initialType = 'task',
  initialData,
  presetRelatedEventId = null,
  presetRelatedEventTitle: _presetRelatedEventTitle,
  availableEvents,
  availableChildren,
  availableMembers,
  initialMemberIds,
  disambiguation,
  onSubmit,
  onDelete,
  seriesDelete,
  onBack,
  defaultDate,
}: ItemFormModalProps) {
  // A full-screen takeover, mounted only while shown: hide and pause the
  // bottom toast slot underneath it (YAR-476).
  useRegisterOverlay(true);
  const userTimezone = useUserStore((s) => s.timezone);

  /* ── State ── */
  const [type, setType] = useState<'task' | 'event'>(
    (initialData?.type as 'task' | 'event') ?? initialType,
  );
  const [title, setTitle] = useState(initialData?.title ?? '');
  const [selectedChildren, setSelectedChildren] = useState<string[]>(() => {
    const rawNames = initialData?.child_names ?? [];
    const names =
      rawNames.length > 0
        ? rawNames
        : initialData?.child_name
          ? [initialData.child_name]
          : [];
    // Normalize AI-extracted names to the family child's canonical
    // name so the dropdown checkbox reflects the binding and the
    // selected pill picks up the right avatar. Names that don't
    // resolve to a family child are dropped: the dropdown only lists
    // family children, so an unmatched pill would be un-removable.
    // The "John mentioned" suggestion banner on the parent modal is
    // where users add a brand-new child if they want one. Honors the
    // user's disambiguation pick when the matcher would otherwise be
    // ambiguous (siblings sharing a first name).
    const canonical: string[] = [];
    const seen = new Set<string>();
    for (const n of names) {
      const child = resolveAiChildName(n, availableChildren, disambiguation);
      if (!child) continue;
      if (seen.has(child.name)) continue;
      seen.add(child.name);
      canonical.push(child.name);
    }
    return canonical;
  });
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>(
    () => initialMemberIds ?? [],
  );
  const [membersDropdownOpen, setMembersDropdownOpen] = useState(false);
  const membersRef = useRef<HTMLDivElement>(null);
  const [dateEnabled, setDateEnabled] = useState(() => {
    // An event always carries a date, so the toggle starts (and stays) on —
    // YAR-130 AC-4 / YAR-137 AC-5. This branch runs first on purpose: a
    // legacy event row with no datetimes would otherwise open with the
    // toggle off *and* disabled, leaving the user unable to turn it back on
    // (YAR-378).
    if (((initialData?.type as 'task' | 'event') ?? initialType) === 'event') {
      return true;
    }
    if (initialData) {
      return !!(
        initialData.start_datetime ||
        initialData.end_datetime ||
        initialData.due_datetime
      );
    }
    if (defaultDate) return true;
    return false;
  });
  // All-day / start / end (shared with the Review v0 DateTimeSheet).
  // Tasks carry their date on due_datetime, not start_datetime.
  const {
    allDay,
    setAllDay,
    startDate,
    changeStartDate,
    startTime,
    setStartTime,
    endDate,
    setEndDate,
    endTime,
    setEndTime,
    combineDatetime,
    orderErrors,
  } = useDateTimeFields(
    {
      start: initialData?.start_datetime ?? initialData?.due_datetime ?? null,
      end: initialData?.end_datetime ?? null,
      allDay: !!initialData?.is_time_unknown,
      defaultDate,
    },
    userTimezone,
  );
  const [location, setLocation] = useState(initialData?.location ?? '');
  const [links, setLinks] = useState<LinkEntry[]>(() => {
    const raw = initialData?.links;
    if (Array.isArray(raw) && raw.length > 0) {
      return raw as unknown as LinkEntry[];
    }
    // Fallback for rows not yet backfilled into the array shape.
    return linksFromScalar(initialData?.link ?? null);
  });
  const [relatedEventId, setRelatedEventId] = useState(
    initialData?.related_event_id ?? presetRelatedEventId ?? '',
  );
  const [notes, setNotes] = useState(initialData?.description ?? '');

  const [validationErrors, setValidationErrors] = useState<{
    title: boolean;
    startDate: boolean;
    dateOrder: boolean;
    timeOrder: boolean;
    startTime: boolean;
  }>({
    title: false,
    startDate: false,
    dateOrder: false,
    timeOrder: false,
    startTime: false,
  });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showValidationBanner, setShowValidationBanner] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  /** null = closed; open with the two counts it shows. */
  const [seriesCounts, setSeriesCounts] = useState<{
    following: number;
    all: number;
  } | null>(null);
  const [deleteScope, setDeleteScope] = useState<DeleteScope>('this');
  const [seriesBusy, setSeriesBusy] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [childrenDropdownOpen, setChildrenDropdownOpen] = useState(false);

  const childrenRef = useRef<HTMLDivElement>(null);

  /* ── Unsaved-changes tracking (YAR-380) ──
     Every editable value is folded into one comparable snapshot; the value
     taken on the first render is the baseline. "Dirty" is therefore any field
     that differs from what the form opened with — the AI-extracted original in
     Review, the saved row everywhere else (YAR-112 AC-10). Comparing against a
     baseline rather than counting keystrokes means typing a character and
     deleting it again correctly reads as clean. */
  const formSnapshot = JSON.stringify({
    type,
    title,
    selectedChildren,
    selectedMemberIds,
    dateEnabled,
    allDay,
    startDate,
    startTime,
    endDate,
    endTime,
    location,
    links,
    relatedEventId,
    notes,
  });
  const pristineSnapshotRef = useRef<string | null>(null);
  if (pristineSnapshotRef.current === null) {
    pristineSnapshotRef.current = formSnapshot;
  }
  const isDirty = formSnapshot !== pristineSnapshotRef.current;

  // Type is switchable when creating; when editing, only for staged review
  // items (allowTypeChange) — committed tasks/events can't convert (YAR-272).
  const typeEditable = mode === 'create' || allowTypeChange;

  // Events require a date, so their Date & Time toggle renders locked on and
  // cannot be switched off (YAR-130 AC-4, YAR-137 AC-5, YAR-378).
  const dateToggleLocked = type === 'event';

  /* ── Required fields gate the Save button (YAR-110 AC-4, YAR-111 AC-7,
     YAR-112 AC-7): a task needs a Title, an event a Title and a Start date.
     Mirrors the `title` / `startDate` checks in handleSubmit, which stay as
     the backstop. Everything else (range order, event start time) is still
     reported on submit — the tickets do not ask for those to disable Save. */
  const missingRequired =
    !title.trim() || (type === 'event' && dateEnabled && !startDate);

  /* ── Switching type mirrors creation's field rules (YAR-112 AC-4): Event
     locks Date & Time on, Task leaves it optional. Related Event auto-hides
     for Events via the `type === 'task'` render guard below. Runs on user
     action only (not mount), so an existing item's loaded date isn't
     clobbered. Replaces the old create-only effect. ── */
  const handleTypeChange = useCallback(
    (next: 'task' | 'event') => {
      if (next === type) return;
      setType(next);
      if (next === 'event') {
        setDateEnabled(true);
      } else if (mode === 'create' && !defaultDate) {
        // Fresh task defaults Date & Time off (matches create); in edit we
        // keep the current value so an existing due date isn't wiped.
        setDateEnabled(false);
      }
    },
    [type, mode, defaultDate],
  );

  /* ── Close children dropdown on outside click ── */
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        childrenRef.current &&
        !childrenRef.current.contains(e.target as Node)
      ) {
        setChildrenDropdownOpen(false);
      }
    }
    if (childrenDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [childrenDropdownOpen]);

  /* ── Close members dropdown on outside click ── */
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        membersRef.current &&
        !membersRef.current.contains(e.target as Node)
      ) {
        setMembersDropdownOpen(false);
      }
    }
    if (membersDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [membersDropdownOpen]);

  /* ── Handlers ── */
  const toggleChild = useCallback((name: string) => {
    setSelectedChildren((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
  }, []);

  const toggleMember = useCallback((userId: string) => {
    setSelectedMemberIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  }, []);

  /* Drop a stale ordering error as soon as the user touches any of the four
     values that produce it, so the red helper never outlives the problem. */
  const clearOrderErrors = useCallback(() => {
    setValidationErrors((prev) =>
      prev.dateOrder || prev.timeOrder
        ? { ...prev, dateOrder: false, timeOrder: false }
        : prev,
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    setSaveError(null);
    const order = orderErrors();
    const errors = {
      title: !title.trim(),
      startDate: type === 'event' && dateEnabled && !startDate,
      // Range order (YAR-382), only while the date fields are in play.
      dateOrder: dateEnabled && order.dateOrder,
      timeOrder: dateEnabled && order.timeOrder,
      startTime:
        type === 'event' && dateEnabled && !allDay && !!startDate && !startTime,
    };
    if (
      errors.title ||
      errors.startDate ||
      errors.dateOrder ||
      errors.timeOrder ||
      errors.startTime
    ) {
      setValidationErrors(errors);
      setShowValidationBanner(true);
      return;
    }

    const startVal = dateEnabled ? combineDatetime(startDate, startTime) : null;
    const endVal = dateEnabled ? combineDatetime(endDate, endTime) : null;

    setShowValidationBanner(false);
    setSaving(true);
    try {
      await onSubmit({
        type,
        title: title.trim(),
        child_names: selectedChildren,
        member_user_ids: selectedMemberIds,
        description: notes.trim() || null,
        start_datetime: startVal,
        end_datetime: endVal,
        due_datetime: type === 'task' && dateEnabled ? startVal : null,
        location: location.trim() || null,
        links: links
          .map((l) => ({
            title: l.title?.trim() || null,
            url: normalizeUrl(l.url),
          }))
          .filter((l) => l.url.length > 0),
        related_event_id: type === 'task' ? relatedEventId || null : null,
        // An all-day item has no stated hour, exactly like an item with no
        // date at all. `combineDatetime` emits a bare "YYYY-MM-DD" for it,
        // which `toIsoWithTz` anchors at local NOON as a day-boundary guard —
        // so without `|| allDay` this wrote `false` and the review card then
        // rendered that noon as "12:00 PM", inventing an hour the sender
        // never wrote (the very thing the flag exists to prevent).
        //
        // The read side above already honours the flag: an all-day item opens
        // with the hour hidden. Writing `false` on save meant a round trip
        // that changed only the title silently destroyed it (YAR-378).
        is_time_unknown: !dateEnabled || allDay,
      });
    } catch (err) {
      console.error('Failed to save item:', err);
      setSaveError(
        err instanceof Error
          ? err.message
          : 'Failed to save. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  }, [
    title,
    selectedChildren,
    selectedMemberIds,
    type,
    notes,
    dateEnabled,
    allDay,
    startDate,
    startTime,
    endDate,
    endTime,
    location,
    links,
    relatedEventId,
    onSubmit,
    combineDatetime,
    orderErrors,
  ]);

  /* ── Leaving the form ──
     Every dismiss vector routes through here. With unsaved changes it raises
     the confirmation stacked over the still-visible form (YAR-112 AC-11: do
     not flatten into a single modal); with a pristine form it closes straight
     through, no dialog (AC-14). Both the back chevron and the backdrop are
     wired to it — the backdrop dropped edits just as silently. */
  const handleBackRequest = useCallback(() => {
    if (isDirty) {
      setShowDiscardModal(true);
      return;
    }
    onBack();
  }, [isDirty, onBack]);

  const handleConfirmDiscard = useCallback(() => {
    setShowDiscardModal(false);
    onBack();
  }, [onBack]);

  const handleDelete = useCallback(async () => {
    if (!onDelete) return;
    try {
      await onDelete();
    } catch (err) {
      console.error('Failed to delete item:', err);
      setShowDeleteModal(false);
      setSaveError(
        err instanceof Error
          ? err.message
          : 'Failed to delete. Please try again.',
      );
    }
  }, [onDelete]);

  /*
   * The trash can. A one-off event (or task) gets the plain confirmation; an
   * event of a series asks what to delete, with each scope's count read
   * first so the dialog says how many sessions go.
   */
  const handleRequestDelete = useCallback(async () => {
    if (!seriesDelete) {
      setShowDeleteModal(true);
      return;
    }
    if (seriesBusy) return;
    setSeriesBusy(true);
    setSaveError(null);
    try {
      const [following, all] = await Promise.all([
        seriesDelete.countFollowing(),
        seriesDelete.countAll(),
      ]);
      setDeleteScope('this');
      setSeriesCounts({ following, all });
    } catch (err) {
      console.error('Failed to count series sessions:', err);
      setSaveError(RECURRENCE_COPY.deleteSeriesCountError);
    } finally {
      setSeriesBusy(false);
    }
  }, [seriesDelete, seriesBusy]);

  const handleConfirmSeriesDelete = useCallback(async () => {
    if (!seriesDelete || seriesBusy) return;
    if (deleteScope === 'this') {
      setSeriesCounts(null);
      await handleDelete();
      return;
    }
    setSeriesBusy(true);
    try {
      if (deleteScope === 'following') await seriesDelete.deleteFollowing();
      else await seriesDelete.deleteAll();
    } catch (err) {
      console.error('Failed to delete series sessions:', err);
      setSeriesCounts(null);
      setSaveError(RECURRENCE_COPY.deleteSeriesError);
    } finally {
      setSeriesBusy(false);
    }
  }, [seriesDelete, seriesBusy, deleteScope, handleDelete]);

  /* ── Derived ── */
  const hasAiBadge = mode === 'edit' && initialData?.extraction_id != null;

  const eventOptions = availableEvents.map((ev) => ({
    value: ev.id,
    label: ev.title,
  }));

  return (
    <div
      className="ph-no-capture fixed inset-0 z-[200] flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="item-form-heading"
      onClick={handleBackRequest}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[5px]" />

      {/* Content card — near-full viewport pinned to bottom */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-card text-card-foreground rounded-t-xl shadow-[0px_0px_16px_-4px_rgba(56,64,74,0.1)] border border-border border-b-0 w-full max-w-2xl h-[calc(100dvh-24px)] flex flex-col"
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-2 shrink-0">
          {/* Left: back */}
          <button
            type="button"
            onClick={handleBackRequest}
            className="inline-flex items-center justify-center rounded-md p-2.5 text-foreground hover:bg-muted transition-colors duration-150 cursor-pointer"
            aria-label="Go back"
          >
            <ChevronLeft size={24} />
          </button>

          {/* Center: title */}
          <h1
            id="item-form-heading"
            className="text-base font-bold text-foreground"
          >
            {mode === 'create' ? 'New Item' : 'Edit Details'}
          </h1>

          {/* Right: actions */}
          <div className="flex items-center gap-2">
            {mode === 'edit' && onDelete && (
              <button
                type="button"
                onClick={() => void handleRequestDelete()}
                disabled={seriesBusy}
                className="inline-flex items-center justify-center rounded-md p-1 text-muted-foreground hover:text-danger hover:bg-muted transition-colors duration-150 cursor-pointer disabled:cursor-default disabled:opacity-50"
                aria-label="Delete item"
              >
                <Trash2 size={20} />
              </button>
            )}
            <Button
              variant="primary"
              size="sm"
              loading={saving}
              disabled={missingRequired}
              onClick={handleSubmit}
            >
              {mode === 'create' ? 'Create' : 'Save'}
            </Button>
          </div>
        </div>

        {/* ── Body (scrollable) ──
            The bottom breathing room lives on the inner content wrapper, NOT the
            scroll container's padding: iOS Safari drops a flex scroll container's
            own padding-bottom from the scrollable area. The bottom is
            max(20px, safe-area-inset): the card is pinned to the bottom of 100dvh
            (items-end), so on a notched iPhone a flat 20px sits behind the home
            indicator and "Add link" reads as flush against the edge. env() is 0 on
            non-notch viewports, preserving the Figma spacing/component/xl (20px).
            Mirrors how the onboarding footers / TimePicker sheet reserve the inset. */}
        <div className="overflow-y-auto flex-1">
          <div className="flex flex-col gap-5 px-5 pt-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
            {/* Type segmented toggle — pill shaped */}
            <div className="flex justify-center">
              <div className="inline-flex items-center gap-3 p-1.5 rounded-full bg-card border border-border">
                <button
                  type="button"
                  onClick={() => handleTypeChange('task')}
                  disabled={!typeEditable}
                  aria-pressed={type === 'task'}
                  className={[
                    'py-1.5 px-3 rounded-full text-sm font-normal transition-colors duration-150',
                    typeEditable ? 'cursor-pointer' : 'cursor-default',
                    type === 'task'
                      ? 'bg-surface-muted text-foreground'
                      : 'bg-transparent text-foreground',
                    !typeEditable && type !== 'task' ? 'opacity-50' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  Task
                </button>
                <button
                  type="button"
                  onClick={() => handleTypeChange('event')}
                  disabled={!typeEditable}
                  aria-pressed={type === 'event'}
                  className={[
                    'py-1.5 px-3 rounded-full text-sm font-normal transition-colors duration-150',
                    typeEditable ? 'cursor-pointer' : 'cursor-default',
                    type === 'event'
                      ? 'bg-surface-muted text-foreground'
                      : 'bg-transparent text-foreground',
                    !typeEditable && type !== 'event' ? 'opacity-50' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  Event
                </button>
              </div>
            </div>

            {/* AI badge — edit mode only */}
            {hasAiBadge && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-light text-primary text-xs font-medium w-fit">
                <Sparkles size={14} />
                <span>Generated by AI</span>
              </div>
            )}

            {/* Validation banner */}
            {showValidationBanner &&
              (validationErrors.title ||
                validationErrors.startDate ||
                validationErrors.dateOrder ||
                validationErrors.timeOrder ||
                validationErrors.startTime) && (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-danger-light border border-danger">
                  <AlertCircle
                    size={16}
                    className="text-danger shrink-0"
                  />
                  {/* One message at a time, in form order: Title sits above
                      Date & Time, so its complaint comes first. */}
                  <span className="text-sm text-danger">
                    {validationErrors.title
                      ? 'Title is required.'
                      : validationErrors.startDate
                        ? 'Please select a start date for this event.'
                        : validationErrors.dateOrder
                          ? DATE_ORDER_MESSAGE
                          : validationErrors.timeOrder
                            ? TIME_ORDER_MESSAGE
                            : 'Please add a start time for this event.'}
                  </span>
                </div>
              )}

            {/* Save error */}
            {saveError && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-danger-light border border-danger">
                <AlertCircle
                  size={16}
                  className="text-danger shrink-0"
                />
                <span className="text-sm text-danger">{saveError}</span>
              </div>
            )}

            {/* a. Title */}
            <Input
              label="Title"
              labelIcon={
                <CaseSensitive
                  size={20}
                  className="text-icon-default"
                />
              }
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setValidationErrors((prev) => ({ ...prev, title: false }));
              }}
              placeholder="Enter title"
              error={validationErrors.title ? 'Required' : undefined}
            />

            {/* b. Children — custom dropdown */}
            <div
              className="flex flex-col gap-2 w-full"
              ref={childrenRef}
            >
              <label className="text-sm font-medium text-foreground leading-5 flex items-center gap-1">
                <User
                  size={20}
                  className="text-icon-default"
                />
                Children
              </label>
              <button
                type="button"
                onClick={() => setChildrenDropdownOpen((prev) => !prev)}
                className={[
                  'w-full min-h-10 rounded-md border bg-background text-foreground text-sm leading-5',
                  'px-3 py-2',
                  'flex items-center gap-2 cursor-pointer',
                  'transition-colors duration-150',
                  'focus:outline-2 focus:outline-offset-0 focus:outline-border-focus focus:border-border-focus',
                  'border-border',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className="flex flex-1 flex-wrap gap-1.5 min-h-[20px]">
                  {selectedChildren.length === 0 && (
                    <span className="text-muted-foreground">
                      Select a child
                    </span>
                  )}
                  {selectedChildren.map((name) => {
                    const child = availableChildren.find(
                      (c) => c.name === name,
                    );
                    return (
                      <span
                        key={name}
                        className="inline-flex items-center gap-1 pl-0.5 pr-2 py-0.5 rounded-full bg-surface-secondary text-foreground text-xs font-medium border border-border"
                      >
                        <ProfileAvatar
                          imagePath={child?.avatar_image_path}
                          emoji={child?.emoji_avatar}
                          color={child?.emoji_color}
                          initials={name.slice(0, 1)}
                          size="sm"
                        />
                        {name}
                      </span>
                    );
                  })}
                </div>
                <ChevronDown
                  size={16}
                  className={[
                    'shrink-0 text-muted-foreground transition-transform duration-150',
                    childrenDropdownOpen ? 'rotate-180' : '',
                  ].join(' ')}
                />
              </button>

              {/* Dropdown checklist */}
              {childrenDropdownOpen && (
                <div className="border border-border rounded-md bg-background shadow-sm">
                  {availableChildren.map((child) => {
                    const isChecked = selectedChildren.includes(child.name);
                    return (
                      <button
                        key={child.name}
                        type="button"
                        onClick={() => toggleChild(child.name)}
                        className="flex items-center gap-3 w-full px-3 py-2.5 text-left hover:bg-surface-secondary transition-colors duration-150 cursor-pointer"
                      >
                        <div
                          className={[
                            'w-4 h-4 rounded-sm border flex items-center justify-center shrink-0',
                            isChecked
                              ? 'bg-primary border-primary'
                              : 'border-border-strong',
                          ].join(' ')}
                        >
                          {isChecked && (
                            <Check
                              size={12}
                              className="text-primary-foreground"
                            />
                          )}
                        </div>
                        <ProfileAvatar
                          imagePath={child.avatar_image_path}
                          emoji={child.emoji_avatar}
                          color={child.emoji_color}
                          initials={child.name.slice(0, 1)}
                          size="sm"
                        />
                        <span className="text-sm text-foreground">
                          {child.name}
                        </span>
                      </button>
                    );
                  })}
                  {availableChildren.length === 0 && (
                    <div className="px-3 py-2.5 text-sm text-muted-foreground">
                      No children available
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* c. Members — multi-select dropdown of household users.
              When availableMembers is omitted (older callers), render the
              read-only "Me" placeholder. */}
            <div
              className="flex flex-col gap-2 w-full"
              ref={membersRef}
            >
              <label className="text-sm font-medium text-foreground leading-5 flex items-center gap-1">
                <Users
                  size={20}
                  className="text-icon-default"
                />
                Members
              </label>
              {availableMembers ? (
                <>
                  <button
                    type="button"
                    onClick={() => setMembersDropdownOpen((prev) => !prev)}
                    className={[
                      'w-full min-h-10 rounded-md border bg-background text-foreground text-sm leading-5',
                      'px-3 py-2',
                      'flex items-center gap-2 cursor-pointer',
                      'transition-colors duration-150',
                      'focus:outline-2 focus:outline-offset-0 focus:outline-border-focus focus:border-border-focus',
                      'border-border',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <div className="flex flex-1 flex-wrap gap-1.5 min-h-[20px]">
                      {selectedMemberIds.length === 0 && (
                        <span className="text-muted-foreground">
                          Select a member
                        </span>
                      )}
                      {selectedMemberIds.map((id) => {
                        const m = availableMembers.find(
                          (x) => x.user_id === id,
                        );
                        if (!m) return null;
                        return (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 pl-0.5 pr-2 py-0.5 rounded-full bg-surface-secondary text-foreground text-xs font-medium border border-border"
                          >
                            <ProfileAvatar
                              imagePath={m.avatar_image_path}
                              emoji={m.avatar_emoji}
                              color={m.avatar_color}
                              initials={m.display_name.slice(0, 1)}
                              size="sm"
                            />
                            {m.display_name}
                          </span>
                        );
                      })}
                    </div>
                    <ChevronDown
                      size={16}
                      className={[
                        'shrink-0 text-muted-foreground transition-transform duration-150',
                        membersDropdownOpen ? 'rotate-180' : '',
                      ].join(' ')}
                    />
                  </button>

                  {membersDropdownOpen && (
                    <div className="border border-border rounded-md bg-background shadow-sm">
                      {availableMembers.map((m) => {
                        const isChecked = selectedMemberIds.includes(m.user_id);
                        return (
                          <button
                            key={m.user_id}
                            type="button"
                            onClick={() => toggleMember(m.user_id)}
                            className="flex items-center gap-3 w-full px-3 py-2.5 text-left hover:bg-surface-secondary transition-colors duration-150 cursor-pointer"
                          >
                            <div
                              className={[
                                'w-4 h-4 rounded-sm border flex items-center justify-center shrink-0',
                                isChecked
                                  ? 'bg-primary border-primary'
                                  : 'border-border-strong',
                              ].join(' ')}
                            >
                              {isChecked && (
                                <Check
                                  size={12}
                                  className="text-primary-foreground"
                                />
                              )}
                            </div>
                            <ProfileAvatar
                              imagePath={m.avatar_image_path}
                              emoji={m.avatar_emoji}
                              color={m.avatar_color}
                              initials={m.display_name.slice(0, 1)}
                              size="sm"
                            />
                            <span className="text-sm text-foreground">
                              {m.display_name}
                            </span>
                          </button>
                        );
                      })}
                      {availableMembers.length === 0 && (
                        <div className="px-3 py-2.5 text-sm text-muted-foreground">
                          No members available
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div
                  className={[
                    'w-full min-h-10 rounded-md border border-border bg-background text-foreground text-sm leading-5',
                    'px-3 py-2',
                    'flex items-center gap-2',
                    'opacity-60 cursor-not-allowed',
                  ].join(' ')}
                >
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-secondary text-foreground text-xs font-medium border border-border">
                    Me
                  </span>
                </div>
              )}
            </div>

            {/* d. Date & Time — toggle section */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-4">
                {/* Locked on for events: an event without a date is not a
                    thing the form may emit. Before YAR-378 this toggle was
                    switchable off, save succeeded with a null start, and the
                    consumer quietly backfilled the currently-selected day —
                    the item landed on the wrong date with no error shown. */}
                <Switch
                  size="lg"
                  checked={dateEnabled}
                  disabled={dateToggleLocked}
                  onChange={() => {
                    if (dateToggleLocked) return;
                    setDateEnabled((prev) => !prev);
                  }}
                />
                <div className="flex items-center gap-1 text-sm font-medium text-foreground">
                  <CalendarClock
                    size={20}
                    className="text-icon-default"
                  />
                  <span>Date &amp; Time</span>
                </div>
              </div>

              {dateEnabled && (
                <div className="flex flex-col gap-3 rounded-md border border-border-strong p-3">
                  <div className="flex items-center gap-4">
                    <Switch
                      size="lg"
                      checked={allDay}
                      onChange={() => {
                        setAllDay((prev) => !prev);
                        // Hiding the time fields retires any time-order
                        // complaint about them.
                        clearOrderErrors();
                      }}
                    />
                    <span className="text-sm font-medium text-foreground">
                      All-day
                    </span>
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-foreground">
                      Start
                    </span>
                    <div className="flex gap-2">
                      {/* min-w-0 on each cell lets the flex children
                        shrink below their intrinsic width on narrow
                        viewports; without it the date+time row
                        overflows the modal on mobile. */}
                      <div className="flex-1 min-w-0">
                        <DatePicker
                          value={startDate}
                          onChange={(next) => {
                            changeStartDate(next);
                            clearOrderErrors();
                          }}
                          placeholder="Select date"
                        />
                      </div>
                      {!allDay && (
                        <div className="flex-1 min-w-0">
                          <TimePicker
                            value={startTime}
                            onChange={(next) => {
                              setStartTime(next);
                              clearOrderErrors();
                            }}
                          />
                        </div>
                      )}
                    </div>
                    {/* Phase 3b: a time read from a vague phrase the family
                      taught; changing it is the correction. */}
                    {!allDay &&
                      isMemoryFilled(
                        initialData?.raw_extraction,
                        'time',
                        startTime,
                      ) && (
                        <span className="text-xs text-muted-foreground">
                          {MEMORY_FILL_CAPTION}
                        </span>
                      )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-foreground">
                      End
                    </span>
                    <div className="flex gap-2">
                      <div className="flex-1 min-w-0">
                        <DatePicker
                          value={endDate}
                          onChange={(next) => {
                            setEndDate(next);
                            clearOrderErrors();
                          }}
                          placeholder="Select date"
                          error={validationErrors.dateOrder}
                        />
                      </div>
                      {!allDay && (
                        <div className="flex-1 min-w-0">
                          <TimePicker
                            value={endTime}
                            onChange={(next) => {
                              setEndTime(next);
                              clearOrderErrors();
                            }}
                            error={validationErrors.timeOrder}
                          />
                        </div>
                      )}
                    </div>
                    {/* Inline helper on the End field (YAR-130 AC-11). The
                        banner above states the same thing; this puts it next
                        to the control the user has to fix. */}
                    {(validationErrors.dateOrder ||
                      validationErrors.timeOrder) && (
                      <p className="text-sm text-text-error">
                        {validationErrors.dateOrder
                          ? DATE_ORDER_MESSAGE
                          : TIME_ORDER_MESSAGE}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* e. Location */}
            <div className="flex flex-col gap-1.5">
              <Input
                label="Location"
                labelIcon={
                  <MapPin
                    size={20}
                    className="text-icon-default"
                  />
                }
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Add location"
              />
              {/* Phase 3b: filled from the family's past edits; editing the
                field is the correction (it teaches Yaruta). */}
              {isMemoryFilled(
                initialData?.raw_extraction,
                'location',
                location,
              ) && (
                <span className="text-xs text-muted-foreground">
                  {MEMORY_FILL_CAPTION}
                </span>
              )}
            </div>

            {/* f. Related Event — task only */}
            {type === 'task' &&
              (availableEvents.length > 0 || relatedEventId) && (
                <Select
                  label="Related Event"
                  labelIcon={
                    <TaskEventDateIcon
                      size={20}
                      className="text-icon-default"
                    />
                  }
                  placeholder="None"
                  value={relatedEventId}
                  onChange={(e) => setRelatedEventId(e.target.value)}
                  options={eventOptions}
                  disabled={!!presetRelatedEventId}
                />
              )}

            {/* h. Notes */}
            <div className="flex flex-col gap-1.5 w-full">
              <label
                htmlFor="item-form-notes"
                className="text-sm font-medium text-foreground leading-5 flex items-center gap-1"
              >
                <FileText
                  size={20}
                  className="text-icon-default"
                />
                Notes
              </label>
              <textarea
                id="item-form-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes"
                rows={5}
                className={[
                  'w-full rounded-md border border-border bg-background text-foreground text-sm leading-5',
                  'px-3 py-2',
                  'placeholder:text-muted-foreground',
                  'focus:outline-2 focus:outline-offset-0 focus:outline-border-focus focus:border-border-focus',
                  'transition-colors duration-150',
                  'resize-none',
                ].join(' ')}
              />
            </div>

            {/* i. Links */}
            <LinksSection
              links={links}
              onChange={setLinks}
            />
          </div>
        </div>
        {/* /Body */}
      </div>
      {/* /Content card */}

      {/* ── Unsaved-changes confirmation (YAR-380) ──
          Distinct from the delete confirmation below: this one guards the
          *exit*, that one destroys the item. Copy and layout follow Figma
          8074:56009 — title only, no body line, an outlined destructive
          "Discard" over a plain "Keep editing". Rendered as a sibling so it
          stacks over the form rather than replacing it, and a backdrop tap
          is not a dismiss vector: the whole point is that leaving must be
          deliberate. Escape routes to "Keep editing". */}
      <Modal
        open={showDiscardModal}
        onClose={() => setShowDiscardModal(false)}
        dismissOnBackdrop={false}
        ariaLabel="Discard new changes?"
        bare
      >
        <div className="flex flex-col items-center gap-4 p-5">
          <p className="text-xl font-medium text-foreground text-center m-0">
            Discard new changes?
          </p>
          <div className="flex flex-col gap-2 w-full">
            <Button
              variant="destructiveOutline"
              fullWidth
              onClick={handleConfirmDiscard}
            >
              Discard
            </Button>
            <button
              type="button"
              onClick={() => setShowDiscardModal(false)}
              className="text-sm font-medium text-foreground hover:text-muted-foreground transition-colors duration-150 py-2 cursor-pointer"
            >
              Keep editing
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Delete confirmation modal ── */}
      <Modal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={`Discard ${type} item?`}
        footer={
          <div className="flex flex-col gap-2 w-full">
            <Button
              variant="destructive"
              fullWidth
              onClick={handleDelete}
            >
              Discard
            </Button>
            <button
              type="button"
              onClick={() => setShowDeleteModal(false)}
              className="text-sm font-medium text-foreground hover:text-muted-foreground transition-colors duration-150 py-2 cursor-pointer"
            >
              Keep editing
            </button>
          </div>
        }
      >
        <p className="text-sm text-muted-foreground">
          This will permanently remove this item from review.
        </p>
      </Modal>

      {/* ── Deleting an event of a series (YAR-443) ── */}
      <Modal
        open={seriesCounts !== null}
        onClose={() => setSeriesCounts(null)}
        title={RECURRENCE_COPY.deleteSeriesTitle}
        footer={
          <div className="flex flex-col gap-2 w-full">
            <Button
              variant="destructive"
              fullWidth
              loading={seriesBusy}
              onClick={() => void handleConfirmSeriesDelete()}
            >
              {RECURRENCE_COPY.deleteSeriesConfirm}
            </Button>
            <button
              type="button"
              onClick={() => setSeriesCounts(null)}
              className="text-sm font-medium text-foreground hover:text-muted-foreground transition-colors duration-150 py-2 cursor-pointer"
            >
              {RECURRENCE_COPY.deleteSeriesCancel}
            </button>
          </div>
        }
      >
        <div
          role="radiogroup"
          aria-label={RECURRENCE_COPY.deleteSeriesTitle}
          className="flex flex-col gap-4"
        >
          {(
            [
              ['this', RECURRENCE_COPY.deleteScopeThis],
              [
                'following',
                RECURRENCE_COPY.deleteScopeCount(
                  RECURRENCE_COPY.deleteScopeFollowing,
                  seriesCounts?.following ?? 0,
                ),
              ],
              [
                'all',
                RECURRENCE_COPY.deleteScopeCount(
                  RECURRENCE_COPY.deleteScopeAll,
                  seriesCounts?.all ?? 0,
                ),
              ],
            ] as const
          ).map(([scope, label]) => (
            <RadioButton
              key={scope}
              name="delete-scope"
              value={scope}
              label={label}
              checked={deleteScope === scope}
              onChange={() => setDeleteScope(scope)}
            />
          ))}
        </div>
      </Modal>
    </div>
  );
}
